import PostPageHtml from "./PostPageHtml.js";
import {createOrgNameUrlTemplate, extractOrgNameFromUrl} from "../../js/util/UrlUtils.js";
import UpdateAccessDialogCmp from "../../components/UpdateAccessDialog/UpdateAccessDialogCmp.js";
import ResourcePermission from "../../js/components/permission/ResourcePermission.js";
import PermissionOnResourceError from "../../js/entities/error/PermissionOnResourceError.js";
import PermissionRepository from "../../js/repository/PermissionRepository.js";
import BaseDialogCmp from "../../components/BaseDialog/BaseDialogCmp.js";
import Post from "../../js/entities/post/Post.js";

export default class PostPageCmp {
	/** @type {PostPageHtml} */
	#html= new PostPageHtml();
	/** @type {HTMLElement} */
	#rootElement;
	/** @type {Post} */
	#post;
	/** @type {PostPageHeaderCmp} */
	#pageHeader;
	/** @type {SecurityContext} */
	#securityContext;
	/** @type {PostPageIoC} */
	#ioc;
	/** @type {Org} */
	#org;

	/**
	 * @param {SecurityContext} securityContext
	 * @param {PostPageIoC} ioc
	 */
	constructor(securityContext, ioc){
		this.#securityContext = securityContext;
		this.#ioc = ioc;
		this.#pageHeader = ioc.getPageHeaderCmp();
	}

	/** @param {HTMLElement} rootElement */
	init(rootElement) {
		this.#rootElement = rootElement;
		this.#html.init(rootElement);
		this.#pageHeader.init(document.querySelector('.page-header'));
		this.#pageHeader.setSecurityContext(this.#securityContext);
	}

	async show(postId) {
		this.#post = Post.parseServerJson(this.#getInitialPost());
		this.#html.show(this.#post);
		this.#html.onEditCb(this.#openEditPage.bind(this, postId));
		this.#html.onShareCb(this.#openSharePermissionsDialog.bind(this));
		this.#pageHeader.onSharePostClick(this.#openSharePermissionsDialog.bind(this));
		this.#pageHeader.onDeletePostClick(this.#onDeletePostClick.bind(this));
		this.#pageHeader.fillBreadcrumbs(this.#post)
	}

	/** @returns {{id: string, title: string, body: string, author: {}, created: number, updated: number, userAccessLevel: number }} */
	#getInitialPost(){
		return JSON.parse(document.querySelector('#post').value)
	}

	/** @param {string} id */
	#openEditPage(id){
		const orgTemplate = createOrgNameUrlTemplate(extractOrgNameFromUrl());
		document.location.assign(`${orgTemplate}/edit-post/${id}`)
	}

	async #openSharePermissionsDialog(){
		if (!this.#org)
			this.#org = await this.#ioc.getOrganizationRepository().getOrganization(extractOrgNameFromUrl())
		const dialog = new UpdateAccessDialogCmp(this.#ioc.getTeamRepository(),
			this.#ioc.getUserRepository(), this.#org.id, this.#securityContext.getCurrentUser().basicUser.id);
		try {
			const resourcePermissions = await this.#ioc.getPermissionRepository()
				.getResourcePermissions(ResourcePermission.RESOURCE_TYPE.POST, [this.#post.id]);
			dialog.show(resourcePermissions);
			dialog.onSaveCb(this.#savePermissions.bind(this))
		} catch (err) {
			if (err instanceof PermissionOnResourceError && err.isResourceForbidden()){
				const newErr = new PermissionOnResourceError(err.resourceId, err.errorCode,
					`You don't have permissions to change sharing options of ${inj.getDisplayName()}`)
				newErr.cause = err;
				throw newErr
			}
			throw err;
		}
	}

	/** @param {ResourcePermission[]} resourcePermissions @param {string} actionType */
	async #savePermissions(resourcePermissions){
		await this.#ioc.getPermissionRepository().changeResourcePermissions(resourcePermissions, PermissionRepository.SAVE_ACTION_TYPE.SAVE);
	}

	async #onDeletePostClick(){
		const dialog = new BaseDialogCmp({btnCancel: "Cancel", btnOk: "Ok"});
		dialog.onOk(() => {
			this.#deletePost();
		});
		dialog.showText(`Are you sure you want to permanently delete post?`);
	}

	async #deletePost(){
		try {
			await this.#ioc.getPostRepository().deletePosts([this.#post.id]);
			window.location.assign(`${createOrgNameUrlTemplate(extractOrgNameFromUrl())}/posts`)
		} catch (err){
			throw err
		}
	}
}